import * as luxon from "luxon";
import { FunctionComponent, ReactNode, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { humaniser } from "@/lib/utils/HumaniseDuration";
import WeekPicker, { getWeekDays } from "@/components/WeekPicker";
import { useWeekStartsOn } from "@/lib/dates/useWeekStartsOn";
import { realignWeekDays } from "@/lib/dates/weekStart";
import * as datefns from "date-fns";
import { Button } from "@/components/ui/button";
import {
  ArrowDown,
  ArrowUp,
  ChartNoAxesCombined,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Clock,
  Dumbbell,
  Minus,
  MoreHorizontal,
  Pencil,
  PlusCircle,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils/ui";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  formatDistance,
  formatTimeSeconds,
  DistanceUnit,
} from "@/lib/utils/exerciseTracking";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useWorkoutSession } from "@/lib/states/workout/hooks";
import type { WorkoutExercise, WorkoutSet } from "@/lib/states/workout/store";
import { createDefaultSet } from "@/lib/utils/workoutSession";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AddWorkoutToPlanDialog from "@/components/client/logs/AddWorkoutToPlanDialog";
import { store } from "@/lib/states/store";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc/client";
type WorkoutSummary = {
  start_time: string;
};

type ClientWorkoutLogsProps = {
  clientId: string;
  workouts?: WorkoutSummary[];
};

type WorkoutHistoryRpcRow = {
  id: string;
  workout_id?: string | null;
  name?: string | null;
  notes?: string | null;
  end_time: string;
  start_time: string;
  with_coach?: boolean | null;
  workout_name?: string | null;
  workout_description?: string | null;
  exercises?: Exercise[];
};

export function ClientWorkoutLogs({
  clientId,
  workouts,
}: ClientWorkoutLogsProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { startWorkoutSession } = useWorkoutSession();
  const org = store.getState().session.org;
  const utils = trpc.useUtils();
  const { data: clientUnit } = trpc.clients.get.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  );
  const weightUnitLabel = clientUnit?.weight_unit === "lbs" ? "lbs" : "kg";
  const distanceUnitLabel: DistanceUnit =
    clientUnit?.distance_unit === "mi" ? "mi" : "km";

  const { data: workoutSummaries } = trpc.workouts.listLogDates.useQuery(
    { clientId, limit: 200 },
    { enabled: Boolean(clientId) && !workouts },
  );

  const workoutDates = useMemo(
    () =>
      (workouts ?? workoutSummaries ?? []).map((workout) => workout.start_time),
    [workouts, workoutSummaries]
  );

  const weekStartsOn = useWeekStartsOn();
  const [selectedDays, setSelectedDays] = useState<Date[]>(() =>
    getWeekDays(new Date(), weekStartsOn),
  );

  useEffect(() => {
    setSelectedDays((current) => realignWeekDays(current, weekStartsOn));
  }, [weekStartsOn]);

  const historyFrom = datefns.addDays(selectedDays[0], -7).toISOString();
  const historyTo = datefns.addDays(selectedDays[6], 1).toISOString();

  const { data, isLoading } = trpc.workouts.history.useQuery(
    {
      clientId,
      startFrom: historyFrom,
      endTo: historyTo,
    },
    {
      enabled: Boolean(clientId && selectedDays.length === 7),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );
  const [workoutToDelete, setWorkoutToDelete] = useState<any>();
  const [workoutToAddToPlan, setWorkoutToAddToPlan] = useState<any>();
  const deleteLog = trpc.workouts.deleteLog.useMutation();

  const currentWeekLogs = useMemo(
    () =>
      data?.filter((log) => {
        const logDate = luxon.DateTime.fromISO(log.start_time);
        return logDate >= luxon.DateTime.fromJSDate(selectedDays[0]);
      }),
    [data, selectedDays]
  );

  const previousWeekLogs = useMemo(
    () =>
      data?.filter((log) => {
        const logDate = luxon.DateTime.fromISO(log.start_time);
        return logDate < luxon.DateTime.fromJSDate(selectedDays[0]);
      }),
    [data, selectedDays]
  );

  const volume = useMemo(() => {
    const currentWeek = currentWeekLogs?.reduce((acc: number, log) => {
      return (
        acc +
        ((log.exercises as Exercise[] | undefined) ?? []).reduce((exerciseAcc: number, exercise: Exercise) => {
          return (
            exerciseAcc +
            (exercise.sets ?? []).reduce((setAcc: number, set: ExerciseSet) => {
              return setAcc + (set.reps ?? 0) * (set.weight ?? 0);
            }, 0)
          );
        }, 0)
      );
    }, 0);
    const previousWeek = previousWeekLogs?.reduce((acc: number, log) => {
      return (
        acc +
        ((log.exercises as Exercise[] | undefined) ?? []).reduce((exerciseAcc: number, exercise: Exercise) => {
          return (
            exerciseAcc +
            (exercise.sets ?? []).reduce((setAcc: number, set: ExerciseSet) => {
              return setAcc + (set.reps ?? 0) * (set.weight ?? 0);
            }, 0)
          );
        }, 0)
      );
    }, 0);

    const percent = previousWeek
      ? (((currentWeek ?? 0) - previousWeek) / previousWeek) * 100
      : 0;
    let percentChange = Math.round(percent * 100) / 100;
    const sign = percentChange > 0 ? 1 : percentChange < 0 ? -1 : 0;
    percentChange = Math.abs(percentChange);

    return { currentWeek, previousWeek, percentChange, sign };
  }, [currentWeekLogs, previousWeekLogs]);

  const logs = useMemo(
    () =>
      currentWeekLogs?.map((workoutLog) => {
        const orderedExercises = [
          ...((workoutLog.exercises as Exercise[] | undefined) ?? []),
        ]
          .sort(
            (a, b) =>
              (a.ex_order ?? 0) - (b.ex_order ?? 0) ||
              (a.sub_order ?? 0) - (b.sub_order ?? 0)
          )
          .map((exercise) => ({
            ...exercise,
            sets: [...(exercise.sets ?? [])].sort(
              (a, b) => (a.set_num ?? 0) - (b.set_num ?? 0)
            ),
          }));
        const start = luxon.DateTime.fromISO(workoutLog.start_time);
        const end = luxon.DateTime.fromISO(workoutLog.end_time);
        const d = luxon.Interval.fromDateTimes(start, end)
          .toDuration()
          .valueOf();

        const duration = humaniser.humanize(d, {
          round: true,
          units: ["h", "m"],
        });

        const newLog = {
          ...workoutLog,
          exercises: orderedExercises,
          duration,
        };

        return newLog;
      }),
    [currentWeekLogs]
  );
  const workoutLogs = logs ?? [];

  const handleEditWorkout = (workoutLog: any) => {
    const clientName = clientUnit
      ? `${clientUnit.first_name ?? ""} ${clientUnit.last_name ?? ""}`.trim()
      : undefined;
    const groupedExercises: WorkoutExercise[][] = [];
    (workoutLog.exercises ?? []).forEach((exercise: Exercise) => {
      const groupIndex = exercise.ex_order ?? 0;
      const exerciseIndex = exercise.sub_order ?? 0;
      const sets = [...exercise.sets].sort(
        (a, b) => (a.set_num ?? 0) - (b.set_num ?? 0)
      );
      const mappedSets: WorkoutSet[] = sets.map((set) => ({
        reps: set.reps ?? null,
        weight: set.weight ?? null,
        time_seconds: set.time_seconds ?? null,
        distance_meters: set.distance_meters ?? null,
      }));
      if (!groupedExercises[groupIndex]) {
        groupedExercises[groupIndex] = [];
      }
      groupedExercises[groupIndex][exerciseIndex] = {
        id: exercise.id,
        ex_order: groupIndex,
        sub_order: exerciseIndex,
        name: exercise.name,
        tracking_type: exercise.tracking_type,
        notes: exercise.notes ?? null,
        sets: mappedSets.length
          ? mappedSets
          : [createDefaultSet(exercise.tracking_type)],
      };
    });

    startWorkoutSession({
      clientId,
      clientName,
      workoutId: workoutLog.workout_id ?? undefined,
      logId: workoutLog.id,
      name: workoutLog.name ?? workoutLog.workout_name ?? "Workout",
      logNotes: workoutLog.notes ?? "",
      exercises: groupedExercises.filter(Boolean),
      startTime: new Date(workoutLog.start_time),
      endTime: workoutLog.end_time ? new Date(workoutLog.end_time) : undefined,
      returnTo: pathname,
    });

    navigate({
      to: "/workout-session",
    });
  };

  const handleDeleteWorkout = (workoutLog: any) => {
    setWorkoutToDelete(workoutLog);
  };

  const handleAddToPlan = (workoutLog: any) => {
    setWorkoutToAddToPlan(workoutLog);
  };

  const handleConfirmDeleteWorkout = async () => {
    if (!workoutToDelete) return;
    try {
      await deleteLog.mutateAsync({
        clientId,
        logId: workoutToDelete.id,
      });
      await utils.workouts.history.invalidate();
      await utils.workouts.listLogDates.invalidate();
      await utils.workouts.listRecentLogs.invalidate();
      setWorkoutToDelete(undefined);
      toast.success("Workout deleted");
    } catch (error) {
      console.error(error);
      toast.error("Error deleting workout");
    }
  };

  const nextWeek = () => {
    setSelectedDays(
      getWeekDays(datefns.addDays(selectedDays[0], 7), weekStartsOn),
    );
  };

  const prevWeek = () => {
    setSelectedDays(
      getWeekDays(datefns.addDays(selectedDays[0], -7), weekStartsOn),
    );
  };

  return (
    <div className="md:w-[90%] mx-auto">
      <AlertDialog
        open={workoutToDelete !== undefined}
        onOpenChange={() => setWorkoutToDelete(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete workout?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently remove the
              workout log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={handleConfirmDeleteWorkout}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AddWorkoutToPlanDialog
        open={Boolean(workoutToAddToPlan)}
        onOpenChange={(open) => {
          if (!open) setWorkoutToAddToPlan(undefined);
        }}
        workoutLog={workoutToAddToPlan}
        clientId={clientId}
        orgId={org?.id}
      />
      <div className="items-center flex flex-row gap-5 justify-center">
        <Button size={"icon"} variant={"outline"} onClick={prevWeek}>
          <ChevronLeft />
        </Button>
        <WeekPicker
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          additionalModifiers={{
            workouts: workoutDates.map((startTime) => new Date(startTime)),
          }}
          additionalClassNames={{
            workouts: "text-orange-500",
          }}
        />
        <Button size={"icon"} variant={"outline"} onClick={nextWeek}>
          <ChevronRight />
        </Button>
      </div>

      <div className="flex flex-row items-center justify-center gap-4">
        <Card className="p-2 mt-4 items-center flex flex-col">
          <p className="items-center">
            <Dumbbell className="size-5 inline self-center mr-2" />
            Weekly Volume
          </p>
          <p>
            {(volume.currentWeek ?? 0).toFixed(2)} {weightUnitLabel}
          </p>
        </Card>
        <Card className="p-2 mt-4 items-center flex flex-col">
          <p className="items-center">
            <ChartNoAxesCombined className="size-5 inline self-center mr-2" />
            Last week
          </p>
          <p
            className={cn(
              "flex flex-row items-center gap-1",
              volume.sign === 1 && "text-green-600",
              volume.sign === -1 && "text-red-600"
            )}
          >
            {volume.sign === 1 && <ArrowUp className="size-5" />}
            {volume.sign === -1 && <ArrowDown className="size-5" />}
            {volume.sign === 0 && <Minus className="size-5" />}
            {volume.percentChange}%
          </p>
        </Card>
      </div>
      {logs?.length === 0 && !isLoading && (
        <p className="flex flex-col items-center pt-12">
          No workouts for this week
        </p>
      )}
      {logs?.length === 0 && isLoading && (
        <div className="flex justify-center items-center m-20">
          <LoadingSpinner className="size-6" />
        </div>
      )}

      <div className="mx-auto mt-4 w-full max-w-5xl">
        {workoutLogs.map((workoutLog) => (
          <WorkoutLogRow
            key={workoutLog.id}
            workoutLog={workoutLog}
            weightUnitLabel={weightUnitLabel}
            distanceUnitLabel={distanceUnitLabel}
            onEditWorkout={() => handleEditWorkout(workoutLog)}
            onAddToPlan={() => handleAddToPlan(workoutLog)}
            onDeleteWorkout={() => handleDeleteWorkout(workoutLog)}
          />
        ))}
      </div>
    </div>
  );
}

interface WorkoutLogRowProps {
  workoutLog: any;
  weightUnitLabel: string;
  distanceUnitLabel: DistanceUnit;
  compact?: boolean;
  onEditWorkout?: () => void;
  onAddToPlan?: () => void;
  onDeleteWorkout?: () => void;
}

export const WorkoutLogRow: FunctionComponent<WorkoutLogRowProps> = ({
  workoutLog,
  weightUnitLabel,
  distanceUnitLabel,
  compact = false,
  onEditWorkout,
  onAddToPlan,
  onDeleteWorkout,
}) => {
  const totalVolume = useMemo(
    () =>
      workoutLog.exercises.reduce((acc: number, exercise: Exercise) => {
        return (
          acc +
          exercise.sets.reduce((acc: number, set: ExerciseSet) => {
            return acc + (set.reps ?? 0) * (set.weight ?? 0);
          }, 0)
        );
      }, 0),
    [workoutLog.exercises]
  );
  const totalSets = useMemo(
    () =>
      workoutLog.exercises.reduce(
        (acc: number, exercise: Exercise) => acc + exercise.sets.length,
        0
      ),
    [workoutLog.exercises]
  );

  const date = new Date(workoutLog.start_time);
  const workoutTitle =
    workoutLog.workout_name ?? datefns.format(date, "cccc") + " Workout";

  return (
    <Card className="workout-log-card my-4 w-full overflow-hidden">
      <div className={cn("border-b", compact ? "p-4" : "p-5")}>
        <div
          className={cn(
            "workout-log-header flex items-start justify-between gap-4",
            compact && "flex-col"
          )}
        >
          <div className="min-w-0 flex-1 basis-0">
            <div className="flex min-w-0 items-center gap-2">
              <h2
                className={cn(
                  "min-w-0 truncate font-bold",
                  compact ? "text-xl" : "text-2xl"
                )}
              >
                {workoutTitle}
              </h2>
              {workoutLog.with_coach && (
                <Badge variant="secondary" className="shrink-0">
                  Coach
                </Badge>
              )}
            </div>
            <p className="mt-1 truncate text-sm text-muted-foreground">
              {datefns.format(date, "ccc d MMM")} · {datefns.format(date, "p")}
            </p>
          </div>
          <div
            className={cn(
              "workout-log-stats min-w-0 gap-0",
              compact
                ? "grid w-full grid-cols-4"
                : "flex shrink-0 items-center justify-end"
            )}
          >
            <WorkoutStat
              compact={compact}
              icon={<Dumbbell className={compact ? "h-4 w-4" : "h-5 w-5"} />}
              value={workoutLog.exercises.length}
              label="Exercises"
            />
            <WorkoutStat
              compact={compact}
              icon={
                <ClipboardList className={compact ? "h-4 w-4" : "h-5 w-5"} />
              }
              value={totalSets}
              label="Sets"
            />
            <WorkoutStat
              compact={compact}
              icon={
                <ChartNoAxesCombined
                  className={compact ? "h-4 w-4" : "h-5 w-5"}
                />
              }
              value={`${totalVolume} ${weightUnitLabel}`}
              label="Volume"
            />
            <WorkoutStat
              compact={compact}
              icon={<Clock className={compact ? "h-4 w-4" : "h-5 w-5"} />}
              value={workoutLog.duration || "0 min"}
              label="Duration"
            />
          </div>
          {workoutLog.with_coach &&
          (onEditWorkout || onAddToPlan || onDeleteWorkout) ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="shrink-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEditWorkout ? (
                  <DropdownMenuItem onClick={onEditWorkout}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                ) : null}
                {onAddToPlan ? (
                  <DropdownMenuItem onClick={onAddToPlan}>
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Add to plan
                  </DropdownMenuItem>
                ) : null}
                {onDeleteWorkout ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={onDeleteWorkout}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      <div className={compact ? "p-4" : "p-5"}>
        {workoutLog.exercises.map((exercise: Exercise) => (
          <ExerciseComponent
            key={`${exercise.ex_order ?? 0}-${exercise.sub_order ?? 0}-${exercise.id}`}
            exercise={exercise}
            weightUnitLabel={weightUnitLabel}
            distanceUnitLabel={distanceUnitLabel}
          />
        ))}
      </div>
    </Card>
  );
};

function WorkoutStat({
  icon,
  value,
  label,
  compact = false,
}: {
  icon: JSX.Element;
  value: ReactNode;
  label: string;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 items-center justify-center border-l py-1 text-muted-foreground first:border-l-0",
        compact
          ? "gap-1.5 px-1.5"
          : "min-w-[120px] gap-2 px-3 sm:gap-3 sm:px-5"
      )}
    >
      <span className="shrink-0">{icon}</span>
      <div className="min-w-0">
        <p
          className={cn(
            "truncate font-semibold text-foreground",
            compact ? "text-sm" : "text-sm sm:text-base"
          )}
        >
          {value}
        </p>
        <p className={cn("truncate", compact ? "text-xs" : "text-xs sm:text-sm")}>
          {label}
        </p>
      </div>
    </div>
  );
}

interface Exercise {
  id: string;
  name: string;
  tracking_type?: string | null;
  sets: ExerciseSet[];
  notes?: string | null;
  ex_order?: number | null;
  sub_order?: number | null;
}

interface ExerciseSet {
  set_num: number;
  exercise_id?: string | null;
  ex_order?: number | null;
  sub_order?: number | null;
  reps?: number | null;
  weight?: number | null;
  time_seconds?: number | null;
  distance_meters?: number | null;
}

interface ExerciseComponentProps {
  exercise: Exercise;
  weightUnitLabel: string;
  distanceUnitLabel: DistanceUnit;
}

const ExerciseComponent: FunctionComponent<ExerciseComponentProps> = ({
  exercise,
  weightUnitLabel,
  distanceUnitLabel,
}) => {
  return (
    <div className="my-2">
      <h3 className="text-lg font-bold">{exercise.name}</h3>
      {exercise.sets.map((set: ExerciseSet) => (
        <div key={set.set_num} className="flex flex-row gap-4">
          <p className="w-3">{set.set_num + 1}</p>
          <p>
            {exercise.tracking_type?.startsWith("distance") &&
              formatDistance(
                set.distance_meters,
                distanceUnitLabel,
                exercise.tracking_type
              )}
            {exercise.tracking_type === "timed" &&
              formatTimeSeconds(set.time_seconds)}
            {(exercise.tracking_type === "strength" ||
              !exercise.tracking_type) && (
              <>
                {set.weight} {weightUnitLabel} x {set.reps}
              </>
            )}
            {exercise.tracking_type === "bodyweight" && (
              <>{set.reps ?? 0} reps</>
            )}
            {exercise.tracking_type?.startsWith("distance") &&
              set.time_seconds && (
                <> in {formatTimeSeconds(set.time_seconds)}</>
              )}
          </p>
        </div>
      ))}
    </div>
  );
};

export default ClientWorkoutLogs;
